import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart3, Search, FileSearch, Users } from 'lucide-react';

interface UserStats {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  total_searches: number;
  today_searches: number;
  active_monitorings: number;
  client_name: string | null;
}

export default function AdminUsageStats() {
  const { t } = useTranslation();
  const { role } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<UserStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({
    totalUsers: 0,
    totalSearches: 0,
    totalMonitorings: 0,
    todaySearches: 0,
  });

  useEffect(() => {
    if (role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchStats();
  }, [role, navigate]);

  const fetchStats = async () => {
    try {
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, full_name');

      if (profilesError) throw profilesError;

      // Fetch all user roles
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');

      if (rolesError) throw rolesError;

      // Fetch all daily searches
      const { data: dailySearches, error: dailyError } = await supabase
        .from('user_daily_searches')
        .select('user_id, search_count, search_date');

      if (dailyError) throw dailyError;

      // Fetch client associations
      const { data: clientUsers, error: clientUsersError } = await supabase
        .from('client_users')
        .select('user_id, client_id');

      if (clientUsersError) throw clientUsersError;

      // Fetch clients
      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('id, name');

      if (clientsError) throw clientsError;

      // Fetch active monitorings per client
      const { data: searches, error: searchesError } = await supabase
        .from('ted_searches')
        .select('client_id, is_active')
        .eq('is_active', true);

      if (searchesError) throw searchesError;

      // Build stats
      const today = new Date().toISOString().split('T')[0];
      const rolesMap = new Map(roles?.map(r => [r.user_id, r.role]) || []);
      const clientUsersMap = new Map(clientUsers?.map(cu => [cu.user_id, cu.client_id]) || []);
      const clientsMap = new Map(clients?.map(c => [c.id, c.name]) || []);
      
      // Count monitorings per client
      const monitoringsPerClient = new Map<string, number>();
      searches?.forEach(s => {
        const count = monitoringsPerClient.get(s.client_id) || 0;
        monitoringsPerClient.set(s.client_id, count + 1);
      });

      // Calculate user stats
      const userStats: UserStats[] = (profiles || []).map(profile => {
        const userSearches = dailySearches?.filter(ds => ds.user_id === profile.id) || [];
        const totalSearches = userSearches.reduce((sum, ds) => sum + ds.search_count, 0);
        const todaySearches = userSearches
          .filter(ds => ds.search_date === today)
          .reduce((sum, ds) => sum + ds.search_count, 0);
        
        const clientId = clientUsersMap.get(profile.id);
        const clientName = clientId ? clientsMap.get(clientId) || null : null;
        const activeMonitorings = clientId ? monitoringsPerClient.get(clientId) || 0 : 0;

        return {
          user_id: profile.id,
          email: profile.email,
          full_name: profile.full_name,
          role: rolesMap.get(profile.id) || 'user',
          total_searches: totalSearches,
          today_searches: todaySearches,
          active_monitorings: activeMonitorings,
          client_name: clientName,
        };
      });

      // Sort by total searches descending
      userStats.sort((a, b) => b.total_searches - a.total_searches);

      setStats(userStats);
      setTotals({
        totalUsers: userStats.length,
        totalSearches: userStats.reduce((sum, u) => sum + u.total_searches, 0),
        totalMonitorings: searches?.length || 0,
        todaySearches: userStats.reduce((sum, u) => sum + u.today_searches, 0),
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeVariant = (userRole: string) => {
    switch (userRole) {
      case 'admin':
        return 'destructive';
      case 'super_user':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const getRoleLabel = (userRole: string) => {
    switch (userRole) {
      case 'admin':
        return 'Admin';
      case 'super_user':
        return 'Super User';
      default:
        return 'Utente';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('admin.usageStats', 'Statistiche di Utilizzo')}
          </h1>
          <p className="text-muted-foreground">
            {t('admin.usageStatsDescription', 'Visualizza le statistiche di utilizzo di tutti gli utenti')}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t('admin.totalUsers', 'Utenti Totali')}
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{totals.totalUsers}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t('admin.totalSearches', 'Ricerche Totali')}
              </CardTitle>
              <Search className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{totals.totalSearches}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t('admin.todaySearches', 'Ricerche Oggi')}
              </CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{totals.todaySearches}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t('admin.activeMonitorings', 'Monitoraggi Attivi')}
              </CardTitle>
              <FileSearch className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{totals.totalMonitorings}</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.userDetails', 'Dettaglio Utenti')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.user', 'Utente')}</TableHead>
                    <TableHead>{t('admin.client', 'Cliente')}</TableHead>
                    <TableHead>{t('admin.role', 'Ruolo')}</TableHead>
                    <TableHead className="text-right">
                      {t('admin.totalSearches', 'Ricerche Totali')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('admin.todaySearches', 'Ricerche Oggi')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('admin.monitorings', 'Monitoraggi')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {user.full_name || user.email}
                          </div>
                          {user.full_name && (
                            <div className="text-sm text-muted-foreground">
                              {user.email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.client_name || (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeVariant(user.role)}>
                          {getRoleLabel(user.role)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {user.total_searches}
                      </TableCell>
                      <TableCell className="text-right">
                        {user.today_searches}
                      </TableCell>
                      <TableCell className="text-right">
                        {user.active_monitorings}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
